/**
 * "Email Sending" settings tab (LT-93).
 *
 * Lists the user's verified senders, lets them add new ones, resend verification,
 * delete, and shows the platform default as a read-only fallback.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, Trash2, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { emailSendersAPI, type VerifiedSender, extractErrorMessage } from '../services/api';
import { EMAIL_PROVIDER_NAME } from '../config/region';

type Toast = { type: 'success' | 'error'; message: string };

const StatusPill: React.FC<{ status: VerifiedSender['status'] }> = ({ status }) => {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-medium">
        <CheckCircle2 className="w-3 h-3" /> Verified
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs font-medium">
        <AlertTriangle className="w-3 h-3" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-medium">
      <Clock className="w-3 h-3" /> Pending verification
    </span>
  );
};

const EmailSendersTab: React.FC<{ setMessage: (m: Toast | null) => void }> = ({ setMessage }) => {
  const [senders, setSenders] = useState<VerifiedSender[]>([]);
  const [platformDefault, setPlatformDefault] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, def] = await Promise.all([
        emailSendersAPI.list(),
        emailSendersAPI.platformDefault().catch(() => ({ email: null })),
      ]);
      setSenders(list);
      setPlatformDefault(def.email);
    } catch (err: any) {
      setMessage({ type: 'error', message: extractErrorMessage(err, 'Failed to load senders') });
    } finally {
      setLoading(false);
    }
  }, [setMessage]);

  useEffect(() => { void load(); }, [load]);

  // Auto-poll status for any pending sender every 10s while the tab is open.
  useEffect(() => {
    const pending = senders.filter((s) => s.status === 'pending');
    if (pending.length === 0) return;
    const id = setInterval(async () => {
      try {
        const refreshed = await Promise.all(
          pending.map((s) => emailSendersAPI.refreshStatus(s.id).catch(() => s)),
        );
        setSenders((prev) => prev.map((s) => refreshed.find((r) => r.id === s.id) || s));
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(id);
  }, [senders]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      const created = await emailSendersAPI.create({
        email: newEmail.trim(),
        from_name: newName.trim() || undefined,
      });
      setSenders((prev) => [created, ...prev]);
      setMessage({ type: 'success', message: `Verification email sent to ${created.email}. Click the link to verify.` });
      setNewEmail('');
      setNewName('');
      setShowAdd(false);
    } catch (err: any) {
      setMessage({ type: 'error', message: extractErrorMessage(err, 'Failed to add sender') });
    } finally {
      setAdding(false);
    }
  };

  const handleResend = async (s: VerifiedSender) => {
    setBusyId(s.id);
    try {
      await emailSendersAPI.resend(s.id);
      setMessage({ type: 'success', message: `Verification email re-sent to ${s.email}.` });
    } catch (err: any) {
      setMessage({ type: 'error', message: extractErrorMessage(err, 'Resend failed') });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (s: VerifiedSender) => {
    if (!window.confirm(`Remove ${s.email} as a sender?`)) return;
    setBusyId(s.id);
    try {
      await emailSendersAPI.remove(s.id);
      setSenders((prev) => prev.filter((x) => x.id !== s.id));
      setMessage({ type: 'success', message: `Removed ${s.email}.` });
    } catch (err: any) {
      setMessage({ type: 'error', message: extractErrorMessage(err, 'Delete failed') });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <p className="text-sm text-gray-300">
          Verify the email addresses you want to send lead emails from. Each address
          gets a one-click verification email from {EMAIL_PROVIDER_NAME}; once verified, that
          address shows up as a "From" choice when you send.
        </p>
      </div>

      {/* Senders list */}
      <div className="rounded-lg border border-slate-700/50 bg-slate-800/30">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/40">
          <h3 className="text-sm font-medium text-gray-200">Your senders</h3>
          <button
            type="button"
            onClick={() => setShowAdd((v) => !v)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add sender
          </button>
        </div>

        {showAdd && (
          <form onSubmit={handleAdd} className="p-3 space-y-2 border-b border-slate-700/40 bg-slate-900/30">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Display name (optional)"
                className="px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={adding || !newEmail.trim()}
                className="px-3 py-1.5 text-sm rounded-md bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50"
              >
                {adding ? 'Sending verification…' : 'Send verification email'}
              </button>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-3 py-1.5 text-sm rounded-md bg-white/10 text-gray-200 hover:bg-white/15"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="p-4 text-sm text-gray-400">Loading…</div>
        ) : senders.length === 0 ? (
          <div className="p-4 text-sm text-gray-400">
            No senders yet. Add one to send from your own email address. Otherwise the platform default below is used.
          </div>
        ) : (
          <ul className="divide-y divide-slate-700/40">
            {senders.map((s) => (
              <li key={s.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">{s.email}</p>
                  <div className="mt-0.5 flex items-center gap-2">
                    <StatusPill status={s.status} />
                    {s.status === 'pending' && (
                      <span className="text-xs text-gray-400">Check your inbox for the verification email.</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {s.status !== 'verified' && (
                    <button
                      type="button"
                      onClick={() => handleResend(s)}
                      disabled={busyId === s.id}
                      title="Resend verification email"
                      className="p-1.5 rounded-md text-gray-400 hover:text-cyan-300 hover:bg-white/5 disabled:opacity-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(s)}
                    disabled={busyId === s.id}
                    title="Remove sender"
                    className="p-1.5 rounded-md text-gray-400 hover:text-red-400 hover:bg-white/5 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Platform default */}
      <div className="rounded-lg border border-slate-700/50 bg-slate-800/30 p-3">
        <h3 className="text-sm font-medium text-gray-200 mb-1">Platform default sender</h3>
        {platformDefault ? (
          <p className="text-sm text-gray-300">
            Used when you don't have a verified sender of your own:{' '}
            <span className="font-medium text-white">{platformDefault}</span>
          </p>
        ) : (
          <p className="text-sm text-amber-300">
            No platform default configured. Until you verify your own sender, lead email sends will fail. Contact your admin.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailSendersTab;
